// The preview-then-apply import, ONCE, for both master imports (code review
// G-10, D-284): References → Vehicles'
// fleet spreadsheet and References → Vendors' Point Click Care list.
//
// The file is parsed on the SERVER. The browser posts it, the API returns the
// plan it would apply — adds, per-field updates, unchanged, absent, duplicates
// and skipped rows — and Apply commits that exact plan by its token, so preview
// and commit cannot disagree about what the file said. A token expires, and an
// expired one comes back as a 409: the master may have changed since, so the
// answer is to preview again rather than retry blindly.
//
// What differs between the two imports — the explainer, the file type, the
// notices and what "absent" means — is the caller's. This file holds the dialog
// frame and the lists both previews show; the flow is `lib/importFlow.ts`.
import type { ReactNode } from 'react'
import {
  Body1,
  Button,
  Caption1,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowImportRegular } from '@fluentui/react-icons'
import { AppDialog } from './AppDialog'
import { FileDropZone } from './FileDropZone'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'
import { ErrorBanner } from './States'
import type { ImportFlow } from '../lib/importFlow'
import { sentenceList } from '../lib/format'

const useStyles = makeStyles({
  surface: { maxWidth: '760px' },
  body: { display: 'flex', flexDirection: 'column', gap: '12px' },
  counts: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  sectionHead: { fontWeight: tokens.fontWeightSemibold, display: 'block', marginTop: '4px' },
  hint: { color: tokens.colorNeutralForeground3, padding: '8px 2px' },
  // `display: block`: `Body1` is an inline span, and an inline box wraps its
  // border line by line rather than around the block.
  warn: {
    display: 'block',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 12px',
    marginBottom: '14px',
  },
  diff: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '30vh',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 10px',
  },
  diffRow: { display: 'grid', gridTemplateColumns: '70px minmax(0, 1fr)', gap: '10px', alignItems: 'baseline' },
  diffTitle: { fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  change: { display: 'block', color: tokens.colorNeutralForeground2 },
})

/** The Import dialog's frame: the explainer and drop zone until a file is
    read, then the caller's preview, and Cancel / Apply N Changes. */
export function ImportDialog<P>({
  open,
  onClose,
  flow,
  intro,
  accept,
  dropLabel,
  dropHint,
  writes,
  onApply,
  children,
  nested,
}: {
  open: boolean
  onClose: () => void
  flow: ImportFlow<P>
  /** What the import does, above the drop zone. */
  intro: ReactNode
  accept: string
  dropLabel: string
  dropHint: string
  /** How many writes Apply makes. */
  writes: number
  /** Apply's click, when it must ask first (the vendor list's second press). */
  onApply?: () => void
  /** The preview, once a file has been read. */
  children: ReactNode
  /** A confirmation dialog of the caller's, rendered inside this surface. */
  nested?: ReactNode
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const close = () => {
    flow.reset()
    onClose()
  }
  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface className={s.surface}>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>Import</DialogTitle>
            {/* The buttons 24px below the body, and the explainer 16px above
                the drop zone (`dialogForm.ts`, D-349). The preview is a report,
                not a form, and keeps its own 12px rhythm. */}
            <DialogContent className={f.beforeActions}>
              {flow.error && <ErrorBanner message={flow.error} />}
              {!flow.preview && (
                <div className={f.stack}>
                  {intro}
                  {/* The Upload screen's drop zone, not a bare file input: click
                      anywhere inside the border, or drop on it (owner item 20,
                      2026-08-28). */}
                  <FileDropZone
                    accept={accept}
                    icon={ArrowImportRegular}
                    label={dropLabel}
                    hint={dropHint}
                    disabled={flow.reading}
                    onFile={flow.onFile}
                  />
                  {flow.reading && <Spinner size="tiny" label={`Reading ${flow.fileName}…`} />}
                </div>
              )}
              {flow.preview && (
                <div className={s.body}>
                  {children}
                  {flow.applying && <Spinner size="tiny" label="Applying…" />}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={close} disabled={flow.applying}>
                Cancel
              </Button>
              {flow.preview && (
                <Button
                  appearance="primary"
                  disabled={flow.applying || writes === 0}
                  onClick={() => (onApply ? onApply() : void flow.apply())}
                >
                  {writes === 0 ? 'Nothing To Write' : `Apply ${writes} ${writes === 1 ? 'Change' : 'Changes'}`}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
        {nested}
      </DialogSurface>
    </AppDialog>
  )
}

/** The preview's counts row: the caller's badges. */
export function ImportCounts({ children }: { children: ReactNode }) {
  const s = useStyles()
  return <div className={s.counts}>{children}</div>
}

/** A yellow notice in the preview. */
export function ImportNotice({ children }: { children: ReactNode }) {
  const s = useStyles()
  return <Body1 className={s.warn}>{children}</Body1>
}

/** The rows the file had that the import could not use. */
export function SkippedRows({ skipped }: { skipped: readonly { row: number; reason: string }[] }) {
  if (skipped.length === 0) return null
  return (
    <ImportNotice>
      {skipped.length} {skipped.length === 1 ? 'row was' : 'rows were'} skipped:{' '}
      {/* The app's one joiner, with the remainder as the last item. */}
      {sentenceList([
        ...skipped.slice(0, 8).map((r) => `row ${r.row} (${r.reason})`),
        ...(skipped.length > 8 ? [`${skipped.length - 8} more`] : []),
      ])}
      .
    </ImportNotice>
  )
}

/** A heading and a comma list, cut at `limit`, with the rest counted. */
export function ImportNames({
  heading,
  names,
  limit,
  total = names.length,
  note,
}: {
  heading: string
  names: readonly string[]
  limit: number
  /** The true count, where `names` is already a sample (the vendor list's absent). */
  total?: number
  note?: ReactNode
}) {
  const s = useStyles()
  if (total === 0) return null
  return (
    <div>
      <Caption1 className={s.sectionHead}>{heading}</Caption1>
      <Body1 className={s.hint}>
        {names.slice(0, limit).join(', ')}
        {total > limit ? ` … and ${total - limit} more` : ''}
      </Body1>
      {note && <Caption1 className={s.hint}>{note}</Caption1>}
    </div>
  )
}

/** The per-field changes, one row per record: "Field: ~~before~~ → after". */
export function ImportChanges<U, F extends string>({
  updates,
  keyOf,
  title,
  changes,
  fieldLabel,
}: {
  updates: readonly U[]
  keyOf: (u: U) => string
  title: (u: U) => string
  changes: (u: U) => readonly { field: F; before: string | null; after: string | null }[]
  fieldLabel: (field: F) => string
}) {
  const s = useStyles()
  if (updates.length === 0) return null
  return (
    <div>
      <Caption1 className={s.sectionHead}>Changes</Caption1>
      <div className={s.diff}>
        {updates.slice(0, 60).map((u) => (
          <div key={keyOf(u)} className={s.diffRow}>
            <span className={s.diffTitle}>{title(u)}</span>
            <span>
              {changes(u).map((c) => (
                <Caption1 key={c.field} className={s.change}>
                  {fieldLabel(c.field)}: <s>{c.before || '(blank)'}</s> → <strong>{c.after || '(blank)'}</strong>
                </Caption1>
              ))}
            </span>
          </div>
        ))}
        {updates.length > 60 && <Caption1 className={s.hint}>… and {updates.length - 60} more</Caption1>}
      </div>
    </div>
  )
}

/** A quiet line under the preview. */
export function ImportHint({ children }: { children: ReactNode }) {
  const s = useStyles()
  return <Caption1 className={s.hint}>{children}</Caption1>
}
